import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useRef } from 'react';
import * as styles from './ModalContainer.module.scss';
import ZoomInAnimation from './ZoomInAnimation';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { UIActions } from '../ui/uiSlice';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useShortcut from '../utils/useShortcut';

const TRANSITION_CONFIG = {
  type: 'spring',
  stiffness: 1000,
  mass: 1,
  damping: 50,
};

interface ModalRef {
  allowClose?: () => boolean;
}

interface ModalContainerProps {
  disableAnimation?: boolean;
}

interface BaseModalContainerProps {
  open: boolean;
  disableAnimation?: boolean;
  alignToTop?: boolean;
  children: React.ReactNode;
  sidebarAware?: boolean;
  underlayType?: 'default' | 'interactiveSidebar' | 'carouselSlider';
}

export const BaseModalContainer = ({
  open,
  disableAnimation,
  alignToTop,
  children,
  sidebarAware,
  underlayType,
}: BaseModalContainerProps) => {
  const isSidebarOpen = useIsSidebarOpen();
  const isNextToSidebar = sidebarAware && isSidebarOpen;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{
            opacity: 0,
            pointerEvents: 'none',
          }}
          animate={{
            opacity: 1,
            pointerEvents: 'auto',
          }}
          exit={{
            opacity: 0,
            // we don't want to be able to click through the modal while it's animating in or out
            // as we could accidentially double click on any confirming buttons, that triggered a close
            pointerEvents: 'none',
          }}
          transition={disableAnimation ? { duration: 0 } : TRANSITION_CONFIG}
          className={classNames(styles.container, {
            [styles.isNextToSidebar]: isNextToSidebar,
          })}
        >
          <ZoomInAnimation
            disabled={disableAnimation}
            className={classNames(styles.inner, {
              [styles.shouldAlignToTop]: alignToTop,
            })}
          >
            {children}
          </ZoomInAnimation>
          <motion.div
            className={classNames(styles.underlay, {
              [styles.carouselSlider]: underlayType === 'carouselSlider',
              [styles.interactiveSidebar]:
                isNextToSidebar && underlayType === 'interactiveSidebar',
            })}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ModalContainer = ({ disableAnimation }: ModalContainerProps) => {
  const {
    isOpen,
    View,
    props: {
      close: onClose,
      disableOutsideClose: shouldDisableOutsideClose,
      alignToTop: shouldAlignToTop,
      underlayType,
      ...props
    },
  } = useAppSelector((state) => state.ui.modal);
  const dispatch = useAppDispatch();
  const modalRef = useRef<ModalRef>();

  const close = () => {
    if (
      modalRef.current &&
      typeof modalRef.current.allowClose === 'function' &&
      !modalRef.current.allowClose()
    ) {
      return;
    }

    if (onClose) {
      onClose();
    } else {
      dispatch(UIActions.closeModal());
    }
  };

  useShortcut(
    'esc',
    (e) => {
      if (!shouldDisableOutsideClose) {
        e.preventDefault();
        close();
      }
    },
    { global: true, skip: !isOpen },
  );

  return (
    <BaseModalContainer
      open={isOpen}
      disableAnimation={disableAnimation}
      alignToTop={shouldAlignToTop}
      underlayType={underlayType}
    >
      {View && (
        <ClickOutsideHandler
          className={styles.content}
          onClickOutside={shouldDisableOutsideClose ? undefined : close}
        >
          <View close={close} modalRef={modalRef} {...props} />
        </ClickOutsideHandler>
      )}
    </BaseModalContainer>
  );
};

export default ModalContainer;
